import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'

const AGORA_ACCENT = '#856FC8'

interface CurriculumQuest {
  id: string
  title: string
  type: string
  status: string
  difficulty: string
  description?: string
  tier?: string
  order: number
  prerequisites?: string[]
  locked?: boolean
  current?: boolean
}

interface Curriculum {
  id: string
  title: string
  description: string
  persona: string
  createdBy: string
  createdAt: string
  quests: CurriculumQuest[]
  currentQuestIndex: number
  completedCount: number
  totalCount: number
}

export default function CurriculumDetailPage() {
  const { curriculumId } = useParams<{ curriculumId: string }>()
  const navigate = useNavigate()
  const [curriculum, setCurriculum] = useState<Curriculum | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadCurriculum = useCallback(async () => {
    try {
      const res = await fetch(`/api/curricula/${curriculumId}`)
      if (!res.ok) throw new Error(`${res.status}`)
      setCurriculum(await res.json())
      setError(null)
    } catch (e: any) {
      setError(e.message || 'Failed to load curriculum')
    } finally {
      setLoading(false)
    }
  }, [curriculumId])

  useEffect(() => { loadCurriculum() }, [loadCurriculum])

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading curriculum...</div>
  if (error || !curriculum) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Curriculum not found ({error})</div>

  const progressPct = curriculum.totalCount > 0
    ? Math.round((curriculum.completedCount / curriculum.totalCount) * 100)
    : 0

  const tiers = new Map<string, CurriculumQuest[]>()
  for (const q of curriculum.quests) {
    const tier = q.tier || 'Quests'
    if (!tiers.has(tier)) tiers.set(tier, [])
    tiers.get(tier)!.push(q)
  }

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 24px' }}>
        <Link to="/evolve" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none', display: 'block', marginBottom: 12 }}>
          ← Back to Evolve
        </Link>

        {/* Header */}
        <div style={{
          marginBottom: 24, padding: '20px 24px',
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 14, borderTop: `3px solid ${AGORA_ACCENT}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
            <PersonaAvatar name={curriculum.persona} size={40} />
            <div style={{ flex: 1 }}>
              <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 24, fontWeight: 700, margin: '0 0 6px 0' }}>
                {curriculum.title}
              </h1>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {capitalize(curriculum.persona)} · Created by {capitalize(curriculum.createdBy)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: progressPct === 100 ? '#5AC884' : AGORA_ACCENT }}>
                {progressPct}%
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {curriculum.completedCount}/{curriculum.totalCount} quests
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${AGORA_ACCENT}, #5AC884)`,
              borderRadius: 3, transition: 'width 0.5s',
            }} />
          </div>
        </div>

        {/* Description */}
        {curriculum.description && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: 20, marginBottom: 20,
          }}>
            <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
              <MarkdownRenderer content={curriculum.description} />
            </div>
          </div>
        )}

        {/* Wizard Box — collapsible roll-down above flow chart */}
        <CurriculumWizardBox quests={curriculum.quests} persona={curriculum.persona} curriculumId={curriculum.id} />

        {/* Flow Chart — full width */}
        <CurriculumFlowChart quests={curriculum.quests} onQuestClick={(q) => {
          const isLocked = q.locked === true || q.status === 'locked'
          if (!isLocked) navigate(`/evolve/quests/${q.id}`)
        }} />
      </div>
    </div>
  )
}

function CurriculumFlowChart({ quests, onQuestClick }: { quests: CurriculumQuest[]; onQuestClick: (q: CurriculumQuest) => void }) {
  const questMap = new Map(quests.map(q => [q.id, q]))

  const layers: CurriculumQuest[][] = []
  const questLayer = new Map<string, number>()

  // Topological sort into layers
  function getLayer(q: CurriculumQuest): number {
    if (questLayer.has(q.id)) return questLayer.get(q.id)!
    const prereqs = (q.prerequisites || []).filter(id => questMap.has(id))
    if (prereqs.length === 0) {
      questLayer.set(q.id, 0)
      return 0
    }
    const maxPrereqLayer = Math.max(...prereqs.map(id => getLayer(questMap.get(id)!)))
    const layer = maxPrereqLayer + 1
    questLayer.set(q.id, layer)
    return layer
  }

  for (const q of quests) getLayer(q)
  const maxLayer = Math.max(...[...questLayer.values()], 0)
  for (let i = 0; i <= maxLayer; i++) layers.push([])
  for (const q of quests) layers[questLayer.get(q.id) || 0].push(q)

  const NODE_W = 180
  const NODE_H = 60
  const LAYER_GAP = 80
  const NODE_GAP = 16

  const nodePositions = new Map<string, { x: number; y: number }>()
  let currentY = 0

  for (let li = 0; li < layers.length; li++) {
    const layer = layers[li]
    const totalWidth = layer.length * NODE_W + (layer.length - 1) * NODE_GAP
    const startX = Math.max(0, (900 - totalWidth) / 2)
    for (let qi = 0; qi < layer.length; qi++) {
      nodePositions.set(layer[qi].id, {
        x: startX + qi * (NODE_W + NODE_GAP),
        y: currentY,
      })
    }
    currentY += NODE_H + LAYER_GAP
  }

  const svgH = currentY - LAYER_GAP + 20
  const svgW = 900

  // Build edges
  const edges: { from: { x: number; y: number }; to: { x: number; y: number }; done: boolean }[] = []
  for (const q of quests) {
    const toPos = nodePositions.get(q.id)
    if (!toPos) continue
    for (const prereqId of (q.prerequisites || [])) {
      const fromPos = nodePositions.get(prereqId)
      if (!fromPos) continue
      const prereqQ = questMap.get(prereqId)
      const isDone = prereqQ && ['completed', 'evaluated', 'awarded'].includes(prereqQ.status)
      edges.push({
        from: { x: fromPos.x + NODE_W / 2, y: fromPos.y + NODE_H },
        to: { x: toPos.x + NODE_W / 2, y: toPos.y },
        done: !!isDone,
      })
    }
  }

  return (
    <div style={{ position: 'relative', overflowX: 'auto', padding: '10px 0' }}>
      <svg width={svgW} height={svgH} style={{ position: 'absolute', top: 10, left: 0, pointerEvents: 'none' }}>
        <defs>
          <marker id="arrow-done" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
            <path d="M0,0 L6,2 L0,4" fill="#5AC884" opacity="0.5" />
          </marker>
          <marker id="arrow-locked" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
            <path d="M0,0 L6,2 L0,4" fill="#4B5563" opacity="0.3" />
          </marker>
        </defs>
        {edges.map((e, i) => {
          const midY = (e.from.y + e.to.y) / 2
          return (
            <path
              key={i}
              d={`M${e.from.x},${e.from.y} C${e.from.x},${midY} ${e.to.x},${midY} ${e.to.x},${e.to.y}`}
              fill="none"
              stroke={e.done ? '#5AC884' : '#4B5563'}
              strokeWidth={e.done ? 2 : 1.5}
              opacity={e.done ? 0.5 : 0.25}
              markerEnd={e.done ? 'url(#arrow-done)' : 'url(#arrow-locked)'}
            />
          )
        })}
      </svg>

      <div style={{ position: 'relative', minHeight: svgH }}>
        {quests.map(q => {
          const pos = nodePositions.get(q.id)
          if (!pos) return null
          const isLocked = q.locked === true || q.status === 'locked'
          const isActive = q.current === true || q.status === 'active' || (q.status === 'proposed' && !q.locked)
          const isDone = ['completed', 'evaluated', 'awarded'].includes(q.status)
          const statusColor = isDone ? '#5AC884' : isActive ? '#6FB8D6' : '#4B5563'

          return (
            <div
              key={q.id}
              onClick={() => onQuestClick(q)}
              style={{
                position: 'absolute', left: pos.x, top: pos.y,
                width: NODE_W, height: NODE_H,
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '0 12px',
                borderRadius: 10,
                background: isDone ? 'rgba(92,200,132,0.08)' : isActive ? 'var(--bg-elevated)' : 'var(--bg-surface)',
                border: `1.5px solid ${isDone ? '#5AC88444' : isActive ? `${AGORA_ACCENT}66` : 'var(--border)'}`,
                cursor: isLocked ? 'default' : 'pointer',
                opacity: isLocked ? 0.4 : 1,
                transition: 'all 0.2s',
                boxShadow: isActive ? `0 0 12px ${AGORA_ACCENT}15` : 'none',
              }}
              onMouseEnter={e => { if (!isLocked) e.currentTarget.style.boxShadow = `0 2px 10px rgba(0,0,0,0.2)` }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = isActive ? `0 0 12px ${AGORA_ACCENT}15` : 'none' }}
            >
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                background: isDone ? '#5AC884' : isActive ? AGORA_ACCENT : 'var(--bg-elevated)',
                border: `2px solid ${statusColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: isDone ? 11 : 10, fontWeight: 700,
                color: isDone || isActive ? 'white' : 'var(--text-muted)',
              }}>
                {isDone ? '✓' : isLocked ? '🔒' : q.order}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: isDone ? '#5AC884' : isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                }}>
                  {q.title}
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 1 }}>
                  {isDone ? 'Complete' : isActive ? 'Available' : 'Locked'}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CurriculumWizardBox({ persona, curriculumId }: { quests: CurriculumQuest[]; persona: string; curriculumId: string }) {
  const [actions, setActions] = useState<any[]>([])
  const [alert, setAlert] = useState<{ level: string; message: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`/api/wizard/curriculum/${curriculumId}`)
        if (res.ok) {
          const data = await res.json()
          setActions(data.actions || [])
          setAlert(data.alert || null)
        }
      } catch {}
      setLoading(false)
    }
    load()
    const interval = setInterval(load, 30000)
    return () => clearInterval(interval)
  }, [curriculumId])

  const functionIcons: Record<string, string> = {
    acceptance_enforcer: '📬', deliverable_checker: '📎', stall_detector: '⏸',
    curriculum_navigator: '🧭', evaluation_prompter: '⭐', progress_reporter: '📊',
    prerequisite_coach: '🎓', reporting_assistant: '📝', pattern_detector: '🔍',
    velocity_monitor: '⚡', sleep_aware: '💤',
  }

  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 12, overflow: 'hidden', marginBottom: 16,
      position: 'relative', zIndex: 10,
    }}>
      {/* Collapsed header — always visible, clickable to toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', padding: '10px 16px',
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'inherit', textAlign: 'left',
        }}
      >
        <span style={{
          transition: 'transform 0.2s',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          display: 'inline-block', fontSize: 10, color: 'var(--text-muted)',
        }}>▶</span>
        <span style={{ fontSize: 14 }}>🧙</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Curriculum Wizard
        </span>
        {alert && (
          <span style={{
            fontSize: 10, fontWeight: 600, marginLeft: 8,
            color: alert.level === 'green' ? '#5AC884' : alert.level === 'red' ? '#f85149' : '#E6C85A',
          }}>
            {alert.level === 'green' ? '✓' : alert.level === 'red' ? '!' : '~'} {alert.message}
          </span>
        )}
        {actions.length > 0 && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
            background: 'var(--bg-elevated)', color: 'var(--text-muted)', marginLeft: 4,
          }}>
            {actions.length}
          </span>
        )}
        <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {capitalize(persona)}
        </span>
      </button>

      {/* Activity log — only when expanded */}
      {expanded && <div style={{ maxHeight: 400, overflowY: 'auto', borderTop: '1px solid var(--border)' }}>
        {loading ? (
          <div style={{ padding: '20px 16px', fontSize: 11, color: 'var(--text-muted)' }}>Loading wizard data...</div>
        ) : actions.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 20, opacity: 0.15, marginBottom: 6 }}>🧙</div>
            <div style={{ fontSize: 11 }}>No wizard activity yet</div>
          </div>
        ) : (
          actions.map((action: any, i: number) => {
            const questTitle = action.quest_title || action.quest_id
            return (
              <div key={action.id || i} style={{
                padding: '10px 16px',
                borderBottom: i < actions.length - 1 ? '1px solid var(--border)' : 'none',
                display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }}>
                  {functionIcons[action.function_name] || '🧙'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 9, fontWeight: 600, color: AGORA_ACCENT, marginBottom: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {questTitle}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                    {action.message || action.action}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>
                    {action.created_at ? formatTimestamp(action.created_at) : ''}
                    {action.function_name && (
                      <span style={{ marginLeft: 6, opacity: 0.6 }}>
                        {action.function_name.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>}
    </div>
  )
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
